import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useLayoutEffect, useState, useMemo} from 'react';

import {pluralize} from 'BenchlingUtil';
import {
  PlateMapLegendItem,
  PlateMapWellDisplayLayer,
  GenericPlateMapDisplayParams,
  SCHEMA_FIELD_NUMBER_WELL_COLOR,
  CONCENTRATION_LAYER_WELL_COLOR,
  MeasurementAttribute,
  PlateMapMeasurementType,
  PlateMapDisplayLayerAttribute,
} from 'samples/plate-maps/PlateMapConstants';
import {getLegendItems, hasMeasurementAttributeMixedUnits} from 'samples/plate-maps/PlateMapUtil';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * Legend for a PlateMapView when the display layer is a number display, showing a gradient between
 * two values.
 * @param legendItems Always has exactly 2 legend items, representing min and max.
 * @param gradientColor The base color for the gradient (will be used from 10% to 100% opacity)
 */
function NumberPlateMapLegend({
  legendItems,
  gradientColor,
}: {
  legendItems: Array<PlateMapLegendItem>;
  gradientColor: string;
}): JSX.Element {
  const minItem = legendItems[0];
  const maxItem = legendItems[1];

  // Create dynamic gradient style using the provided color with 10% to 100% opacity
  const gradientStyle = {
    background: `linear-gradient(45deg, ${gradientColor}1A, ${gradientColor})`, // 1A = 10% opacity in hex
    height: '20px',
    width: '100%',
  };

  return (
    <div className={css(styles.legend)}>
      <div className={css(styles.numericLegendLabels)}>
        <span>{minItem.label}</span>
        <span>{maxItem.label}</span>
      </div>
      <div style={gradientStyle} />
    </div>
  );
}

/**
 * Legend for a PlateMapView when the display layer is a number display and has an error message.
 *
 * Shows an error message and a gray bar instead of a gradient for the legend.
 */
function NumberPlateMapLegendWithError({errorMessage}: {errorMessage: string}): JSX.Element {
  return (
    <div className={css(styles.legend)}>
      <div className={css(styles.errorMessage)}>{errorMessage}</div>
      <div className={css(styles.errorBar)} />
    </div>
  );
}

/**
 * Gets the gradient color for the legend for number display layers
 */
function getLegendGradientColor(
  displayLayer: PlateMapWellDisplayLayer,
  attribute: PlateMapDisplayLayerAttribute
): string {
  if (displayLayer === PlateMapWellDisplayLayer.CONCENTRATION) {
    return CONCENTRATION_LAYER_WELL_COLOR;
  } else if (displayLayer === PlateMapWellDisplayLayer.MEASUREMENT) {
    return (attribute as MeasurementAttribute).color;
  } else {
    return SCHEMA_FIELD_NUMBER_WELL_COLOR;
  }
}

/**
 * Renders the legend for a PlateMapView, describing what data different well displays correspond to
 * depending on the given display layer.
 */
export default function PlateMapLegend({
  plateMap,
  displayLayer,
  assignmentGroupsById,
  attribute,
  groupedValidationErrors,
}: GenericPlateMapDisplayParams): JSX.Element | null {
  const {ref, height} = useResizeObserver();
  const [showSubtext, setShowSubtext] = useState(true);
  const legendItems = useMemo(
    () =>
      getLegendItems({
        plateMap,
        displayLayer,
        assignmentGroupsById,
        attribute,
        groupedValidationErrors: groupedValidationErrors ?? {},
      }),
    [plateMap, displayLayer, assignmentGroupsById, attribute, groupedValidationErrors]
  );
  const groupText = displayLayer === PlateMapWellDisplayLayer.WELL_ROLE ? 'group' : 'well';

  useLayoutEffect(() => {
    if (height != null) {
      // If the legend wraps to more than one row, hide the legend subtext
      setShowSubtext(height < 40);
    }
  }, [height]);

  // Check for mixed units in measurement attributes before showing legend items
  const hasMixedUnits =
    displayLayer === PlateMapWellDisplayLayer.MEASUREMENT &&
    hasMeasurementAttributeMixedUnits(plateMap, attribute as MeasurementAttribute);

  // Show error message instead of a legend for measurement attributes with mixed units
  if (hasMixedUnits) {
    const measurementAttribute = attribute as MeasurementAttribute;
    const measurementName =
      measurementAttribute.type === PlateMapMeasurementType.FINAL_CONCENTRATION
        ? 'final concentration'
        : 'transfer quantity';

    return (
      <NumberPlateMapLegendWithError
        errorMessage={`Cannot display a gradient since the ${measurementName} values use different units`}
      />
    );
  }

  // Don't show a legend at all if there are no items
  if (legendItems.length === 0) {
    return null;
  }

  // Show gradient legend for number display layers (number schema fields, concentration layers, and measurements)
  if (
    [
      PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER,
      PlateMapWellDisplayLayer.CONCENTRATION,
      PlateMapWellDisplayLayer.MEASUREMENT,
    ].includes(displayLayer)
  ) {
    const gradientColor = getLegendGradientColor(displayLayer, attribute);
    return <NumberPlateMapLegend legendItems={legendItems} gradientColor={gradientColor} />;
  }

  // TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less
  return (
    <div className={classNames(css(styles.legend), 'plate-map-legend')} ref={ref}>
      {legendItems.map(({label, icon, count, error}, i) => (
        <span className={classNames(css(styles.legendItem), 'plate-map-legend-item')} key={i}>
          <div className={classNames(css(styles.legendItemIcon), 'plate-map-legend-item-icon')}>
            {error ? <BenchlingIcon icon="exclamation-circle" className={css(styles.errorIcon)} /> : icon}
          </div>
          <div className={classNames(css(styles.legendItemText), 'plate-map-legend-item-text')}>
            <p
              className={classNames(css(styles.legendItemName), 'plate-map-legend-item-text-name')}
              title={label}
            >
              {label}
            </p>
            {showSubtext && (
              <p className={css(styles.legendItemSubtext)}>{`${count} ${pluralize(count, groupText)}`}</p>
            )}
          </div>
        </span>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  legend: {
    padding: 8,
    borderRadius: 4,
    border: `1px solid ${Color.GRAY_200}`,
    marginTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    gap: '8px 24px',
  },
  legendItem: {
    display: 'flex',
  },
  legendItemIcon: {
    margin: 'auto',
    width: 20,
    height: 20,
  },
  legendItemText: {
    margin: 'auto 0 auto 8px',
    minWidth: 65,
    lineHeight: '16px',
  },
  legendItemName: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginBottom: 0,
    maxWidth: 175,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  legendItemSubtext: {
    color: Color.GRAY_700,
    marginBottom: 0,
  },
  numericLegendLabels: {
    marginBottom: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    color: Color.GRAY_700,
    width: '100%',
  },
  errorIcon: {
    color: Color.RED_500,
    width: 20,
    height: 20,
  },
  errorMessage: {
    color: Color.GRAY_700,
    fontSize: 14,
    marginBottom: '4px',
    textAlign: 'center',
  },
  errorBar: {
    backgroundColor: Color.GRAY_200,
    height: '20px',
    width: '100%',
    borderRadius: 2,
  },
});
